import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { t } from 'i18next';
import { useCallback, useContext, useState } from 'react';

import TreadLiveMapV2 from '~components/Maps/v2/TreadLiveMapV2';
import OrderAuditLog from '~components/Order/AuditLog/OrderAuditLog';
import { Order } from '~hooks/useOrders/models';
import DriverPayPageContext from '~pages/Approvals/DriverPayV2/DriverPayPageContext';
import DriverPayQueryContext from '~pages/Approvals/DriverPayV2/DriverPayQueryContext';
import { OrderDetails } from '~pages/Dispatch/OrderDetails';

import { ExpandedBlockTabs } from '../../styled';
import { InvoicesQueryContextProvider } from './InvoicesQueryContext';
import InvoicesTab from './InvoicesTab';

enum ExpandedBlockTab {
  INVOICES = 'invoices',
  ORDER_DETAILS = 'order_details',
  LIVE_MAP = 'live_map',
  TIMELINE = 'timeline',
}

const tabs = [
  { key: ExpandedBlockTab.INVOICES, label: t('approvals.driver_pay.tabs.invoices') },
  { key: ExpandedBlockTab.ORDER_DETAILS, label: t('order.form.order_details') },
  { key: ExpandedBlockTab.LIVE_MAP, label: t('live_map.live_map') },
  { key: ExpandedBlockTab.TIMELINE, label: t('dispatch.timeline.timeline') },
];

interface ExpandedBlockProps {
  order: Order;
}

export default function ExpandedBlock({ order }: ExpandedBlockProps) {
  const [selectedTab, setSelectedTab] = useState(ExpandedBlockTab.INVOICES);
  const { currentCategory } = useContext(DriverPayPageContext);
  const { filters } = useContext(DriverPayQueryContext);

  const handleSelectedTabChange = useCallback(
    (__: React.SyntheticEvent, value: ExpandedBlockTab) => {
      setSelectedTab(value);
    },
    [],
  );

  return (
    <Box>
      <Box sx={{ px: 1 }}>
        <ExpandedBlockTabs value={selectedTab} onChange={handleSelectedTabChange}>
          {tabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </ExpandedBlockTabs>
      </Box>

      {selectedTab === ExpandedBlockTab.INVOICES && (
        <InvoicesQueryContextProvider
          order={order}
          category={currentCategory}
          searchQuery={filters.searchQuery}
        >
          <InvoicesTab />
        </InvoicesQueryContextProvider>
      )}

      {selectedTab === ExpandedBlockTab.ORDER_DETAILS && (
        <Box sx={{ p: 1 }}>
          <OrderDetails order={order} />
        </Box>
      )}

      {selectedTab === ExpandedBlockTab.LIVE_MAP && (
        <Box
          sx={(theme) => ({
            bgcolor: theme.palette.background.paper,
            border: 1,
            borderColor: theme.brandV2.colors.treadGray7,
            borderRadius: 1,
            height: 400,
            m: 1,
            overflow: 'hidden',
          })}
        >
          <TreadLiveMapV2 order={order} />
        </Box>
      )}

      {selectedTab === ExpandedBlockTab.TIMELINE && <OrderAuditLog order={order} />}
    </Box>
  );
}
